import type { AnyRouter } from '@tanstack/react-router';

/**
 * The screen the reader was opened from, so its back control can name that
 * screen and return to it. Kept in memory only: a cold load has none, and
 * back goes to the book's page.
 */
export interface Origin {
  pathname: string;
  href: string;
  /** Its place in this tab's history, when the router has numbered it. */
  index?: number;
}

const READER = /^\/books\/[^/]+\/read\//;
const SESSION = '/review/session';

let origin: Origin | null = null;
let started: Origin | null = null;
let history: AnyRouter['history'] | null = null;

const indexOf = (state: unknown): number | undefined => {
  const i = (state as { __TSR_index?: unknown } | undefined)?.__TSR_index;
  return typeof i === 'number' ? i : undefined;
};

/**
 * Notes where each visit to the reader began: the last screen before it that
 * wasn't the reader. Turning to another chapter keeps the same origin.
 *
 * And where a review session was started: the screen it was opened from, by
 * a step forward. Stepping back into a session (from a passage it opened)
 * is a return to it, not a start.
 */
export function watchOrigin(router: AnyRouter): void {
  history = router.history;
  router.subscribe('onBeforeNavigate', ({ fromLocation, toLocation }) => {
    if (!fromLocation) return;
    const from: Origin = {
      pathname: fromLocation.pathname,
      href: fromLocation.href,
      index: indexOf(fromLocation.state),
    };
    if (toLocation.pathname === SESSION) {
      const to = indexOf(toLocation.state);
      const back = from.index !== undefined && to !== undefined && to < from.index;
      if (from.pathname !== SESSION && !back) started = from;
      return;
    }
    if (!READER.test(toLocation.pathname) || READER.test(from.pathname)) return;
    origin = from;
  });
}

/** Where the reader on screen was opened from; null on a cold load. */
export const readerOrigin = (): Origin | null => origin;

/** Where the review session on screen was started from; null on a cold load. */
export const sessionOrigin = (): Origin | null => started;

/**
 * What the back control calls the origin: "Map", "Your answer", "Explore" or
 * "Notes". Null when the book's own title names it: the book's pages, and
 * everywhere else, from which back goes to the book.
 *
 * passage: the reader was opened on a cited passage. Only then is a session
 * "Your answer"; its other way in (Read instead) is just reading the book.
 */
export function originName(o: Origin | null, passage: boolean): string | null {
  const p = (o?.pathname ?? '').replace(/\/$/, '');
  if (p === '/map' || /^\/books\/[^/]+\/graph$/.test(p)) return 'Map';
  if (p === '/review/session') return passage ? 'Your answer' : null;
  if (p === '/explore' || p.startsWith('/explore/')) return 'Explore';
  if (p === '/notes' || /^\/books\/[^/]+\/notes$/.test(p)) return 'Notes';
  return null;
}

/** Whether the origin is one of this book's own pages (its overview, cast, story). */
export const withinBook = (o: Origin | null, bookId: string): boolean =>
  !!o && (o.pathname === `/books/${bookId}` || o.pathname.startsWith(`/books/${bookId}/`));

/**
 * Returns to the origin as the browser's Back would, however many chapters
 * were turned since, so the screen comes back as it was left. When it is no
 * longer behind us in history, it is opened afresh.
 */
export function returnTo(o: Origin): void {
  if (!history) return;
  const now = indexOf(history.location.state);
  if (o.index !== undefined && now !== undefined && o.index < now) history.go(o.index - now);
  else history.push(o.href);
}
